"use client";

import { useState } from "react";
import { ChevronDown, Search } from "lucide-react";

type FaqItem = {
  question: string;
  answer: string;
  category: string;
};

type Category = {
  key: string;
  label: string;
};

type Props = {
  faqs: FaqItem[];
  categories: Category[];
  searchPlaceholder: string;
  noResults: string;
};

export default function FaqAccordion({ faqs, categories, searchPlaceholder, noResults }: Props) {
  const [activeCategory, setActiveCategory] = useState("categoryAll");
  const [searchQuery, setSearchQuery] = useState("");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = activeCategory === "categoryAll" || faq.category === activeCategory;
    const matchesSearch =
      searchQuery === "" ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setOpenIndex(null);
          }}
          className="w-full pl-11 pr-4 py-3.5 border border-gray-200 text-sm text-brand-dark placeholder:text-brand-muted/50 focus:outline-none focus:border-brand-gold/50 focus:shadow-[0_0_0_3px_rgba(165,141,118,0.08)] transition-all duration-300"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto pb-1 -mx-1 px-1">
        {categories.map((cat) => (
          <button
            key={cat.key}
            onClick={() => {
              setActiveCategory(cat.key);
              setOpenIndex(null);
            }}
            className={`px-4 py-2.5 min-h-[44px] text-xs font-medium tracking-wide transition-all duration-300 whitespace-nowrap active:scale-[0.97] ${
              activeCategory === cat.key
                ? "bg-brand-gold text-white shadow-sm"
                : "bg-brand-cream/50 text-brand-muted hover:bg-brand-cream hover:text-brand-dark"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* FAQ Items */}
      <div className="space-y-0 divide-y divide-gray-100">
        {filteredFaqs.length === 0 ? (
          <p className="text-sm text-brand-muted py-8 text-center">{noResults}</p>
        ) : (
          filteredFaqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={index}>
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-start justify-between gap-4 py-4 sm:py-5 text-start group min-h-[44px]"
                >
                  <span className={`text-sm font-medium leading-relaxed transition-colors ${isOpen ? "text-brand-gold" : "text-brand-dark group-hover:text-brand-gold"}`}>
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 flex-shrink-0 mt-0.5 text-brand-muted transition-all duration-300 ${isOpen ? "rotate-180 text-brand-gold" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? "max-h-96 pb-5" : "max-h-0"
                  }`}
                >
                  <p className="text-sm text-brand-muted leading-relaxed pe-8">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
